import { useEffect, useRef } from 'react'
import { useClerk, useOrganization, useUser } from '@clerk/clerk-react'

import { useAppTabs } from '@/hooks/useAppTabs'
import { useChameleonUidHash } from '@/hooks/useChameleonIdentity'
import { useConfiguration } from '@/hooks/useConfiguration'
import { useIsGlobalAdmin } from '@/hooks/useIsGlobalAdmin'
import { useIsSuperUser } from '@/hooks/useIsSuperUser'
import { useMountEffect } from '@/hooks/useMountEffect'
import { chameleon, type ChameleonCompany } from '@/lib/chameleon'

/**
 * Owns the entire Chameleon lifecycle: init + identify once auth resolves, clear
 * on sign-out, and routing for deep links inside experiences.
 *
 * Mounted once in App.tsx beside PostHogTracker — the only slot that has Clerk,
 * the router and the query client all in scope and survives every route change.
 * Renders nothing.
 */
export function ChameleonBridge() {
  const { user, isLoaded: userLoaded, isSignedIn } = useUser()
  const { organization, membership, isLoaded: orgLoaded } = useOrganization()
  const { configuration, loading: configLoading } = useConfiguration()
  const isSuperUser = useIsSuperUser()
  const isGlobalAdmin = useIsGlobalAdmin()
  const uidHash = useChameleonUidHash(user?.id, organization?.id)
  const { openTab } = useAppTabs()
  const clerk = useClerk()

  // The navigate handler is registered once, so it has to read the current
  // openTab rather than the one captured at registration time.
  const openTabRef = useRef(openTab)
  useEffect(() => {
    openTabRef.current = openTab
  }, [openTab])

  // Identify exactly once per user+org, per the "one identify call per session"
  // contract — Clerk hands us a fresh `user` object on every token refresh.
  const identifiedRef = useRef<string | null>(null)
  const navigateBoundRef = useRef(false)

  useMountEffect(() => {
    // Clerk's <UserButton> is a managed component with no sign-out callback and
    // the app has no signOut call site of its own, so this listener is the only
    // hook point for clear(). Per Clerk's own contract: null means signed out,
    // undefined means the session is still loading — so test for null strictly.
    return clerk.addListener(({ session, user: sessionUser }) => {
      if (session === null && sessionUser === null) {
        chameleon.clear()
        identifiedRef.current = null
      }
    })
  })

  useEffect(() => {
    if (!userLoaded || !orgLoaded || !isSignedIn || !user) return
    // Org config carries the only per-tenant capability signals this product has
    // (no plan/tier concept exists). Waiting for it keeps the company payload
    // complete on the first and only identify.
    if (organization && configLoading) return

    // Every authenticated user, by decision — no audience gate. Init is the line
    // that puts someone on Chameleon's MTU quota, so narrowing the audience means
    // guarding this call rather than filtering inside identify. If quota becomes a
    // concern, `isSuperUser` is the first candidate (`if (!isSuperUser)` excludes
    // Careforce staff, typically the heaviest users of the app).
    chameleon.init()

    // Must come after init, not in a mount effect: `on` only exists on the queue
    // object init installs, so registering earlier silently does nothing.
    if (!navigateBoundRef.current) {
      navigateBoundRef.current = true
      chameleon.onNavigate((to) => openTabRef.current(to))
    }

    // A missing or mismatched uid_hash makes Chameleon reject the call, so wait
    // for the server-signed hash. Init above stays unconditional on it: loading
    // the SDK costs no MTU until a user is actually identified, and this keeps
    // the install verifiable where the secret is unset (local dev).
    if (!uidHash) return

    // Config completeness is part of the signature rather than a latch
    // condition: a failed /configuration is permanent for the session (retry:1,
    // no refetch trigger), so gating the latch on it re-identified on every
    // Clerk token refresh forever. This still re-identifies exactly once if the
    // capability flags do turn up later, because the signature changes then.
    const signature = `${user.id}:${organization?.id ?? 'no-org'}:${
      configuration ? 'cfg' : 'no-cfg'
    }`
    if (identifiedRef.current === signature) return
    identifiedRef.current = signature

    // Omitted entirely when there is no active org: Chameleon reads a
    // present-but-null company.uid as a broken account link, and a signed-in user
    // genuinely can have no org here (AgentSelection renders "Select an
    // Organization").
    const company: ChameleonCompany | null = organization
      ? {
          uid: organization.id,
          name: organization.name,
          slug: organization.slug,
          created: organization.createdAt.toISOString(),
          members_count: organization.membersCount,
          david_enabled: configuration?.agents?.david?.enabled,
          hanna_enabled: configuration?.agents?.hanna?.enabled,
          ...configuration?.features,
        }
      : null

    chameleon.identify(
      user.id,
      {
        uid_hash: uidHash,
        email: user.primaryEmailAddress?.emailAddress,
        // Chameleon splits `name` into first/last itself, so we don't send both.
        name: user.fullName,
        created: user.createdAt?.toISOString(),
        role: membership?.role,
        is_careforce_staff: isSuperUser,
        is_org_admin: isGlobalAdmin,
        org_count: user.organizationMemberships.length,
        last_sign_in: user.lastSignInAt?.toISOString(),
      },
      company
    )
  }, [
    userLoaded,
    orgLoaded,
    isSignedIn,
    user,
    organization,
    membership,
    configuration,
    configLoading,
    uidHash,
    isSuperUser,
    isGlobalAdmin,
  ])

  return null
}
